import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConfig } from '../../lib/config';
import { api, type SiteSummary } from '../../lib/api';
import { PLANS, FREE_PLAN, getPlanInfo, formatPlanPrice, type PlanId } from '@sarnia/shared';
import { Icon } from '../../components/ui/Icon';
import './billing.css';

interface BillingStatus {
  planId: string | null;
  status: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

interface MyPlanRequest {
  id: string;
  plan_id: string;
  status: string;
  message: string;
  created_at: string;
}

const requestStatusLabels: Record<string, string> = {
  new: 'Request received - we\'ll be in touch',
  contacted: 'Contacted',
  active: 'Active',
  declined: 'Declined',
};

const requestStatusBadgeClass: Record<string, string> = {
  new: 'sarnia-badge--info',
  contacted: 'sarnia-badge--warning',
  active: 'sarnia-badge--success',
  declined: 'sarnia-badge--neutral',
};

export function BillingPage() {
  const { stripeReady } = useConfig();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [requests, setRequests] = useState<MyPlanRequest[]>([]);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [siteId, setSiteId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [statusData, requestsData, sitesData] = await Promise.all([
          api.getBillingStatus(),
          api.getMyPlanRequests(),
          api.getSites(),
        ]);
        if (!active) return;
        setStatus(statusData);
        setRequests(requestsData.requests);
        setSites(sitesData.sites);
      } catch (err) {
        if (active) setError((err as Error).message || 'Could not load billing details.');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (planParam && (planParam === 'basic-monthly' || planParam === 'premium-monthly')) {
      setSelectedPlanId(planParam);
      setPanelOpen(true);
    }
  }, [planParam]);

  function openPanel(planId: PlanId) {
    setSelectedPlanId(planId);
    setPanelOpen(true);
    setError(null);
    setSuccess(null);
  }

  function closePanel() {
    setPanelOpen(false);
    setSelectedPlanId(null);
    setMessage('');
    setSiteId('');
    setError(null);
    setSuccess(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedPlanId) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      if (stripeReady) {
        const data = await api.createBillingCheckout(selectedPlanId);
        window.location.href = data.url;
      } else {
        const payload: { planId: string; message?: string; siteId?: string } = { planId: selectedPlanId };
        const trimmedMessage = message.trim();
        if (trimmedMessage) payload.message = trimmedMessage;
        if (siteId) payload.siteId = siteId;
        await api.requestPlan(payload);
        setSuccess('Thanks! We\'ve got your request and will email you shortly.');
        setPanelOpen(false);
        setSelectedPlanId(null);
        setMessage('');
        setSiteId('');
      }
    } catch (err) {
      setError((err as Error).message || 'Could not send your plan request.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePortal() {
    setRedirecting(true);
    setError(null);
    try {
      const data = await api.createBillingPortal();
      window.location.href = data.url;
    } catch (err) {
      setError((err as Error).message || 'Could not open billing portal.');
      setRedirecting(false);
    }
  }

  if (loading) {
    return (
      <div className="dash-page bl-page">
        <p className="dash-page-subtitle">Loading plans & billing…</p>
      </div>
    );
  }

  const currentPlan = status ? getPlanInfo(status.planId) : null;
  const hasSubscription = status?.status === 'active' || status?.status === 'trialing';
  const planName = currentPlan?.name ?? FREE_PLAN.name;

  return (
    <div className="dash-page bl-page">
      <div className="dash-page-header">
        <h1>Plans & billing</h1>
        <p className="dash-page-subtitle">Everything is free. Choose a plan only if you want us to handle setup and maintenance.</p>
      </div>

      {error && (
        <div className="sarnia-card bl-alert bl-alert--error" role="alert">
          <Icon name="alert" size={18} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="sarnia-card bl-alert bl-alert--success" role="status">
          <Icon name="check" size={18} />
          <span>{success}</span>
        </div>
      )}

      <section className="sarnia-card bl-status-card">
        <div className="bl-status-card__header">
          <div>
            <p className="bl-status-card__eyebrow">Current plan</p>
            <h2>{planName}</h2>
          </div>
          {hasSubscription && <span className="sarnia-badge sarnia-badge--success">Active</span>}
        </div>
        {hasSubscription ? (
          <>
            <dl className="bl-facts">
              <div>
                <dt>Status</dt>
                <dd>{status?.status}</dd>
              </div>
              {status?.currentPeriodEnd && (
                <div>
                  <dt>Current period ends</dt>
                  <dd>{new Date(status.currentPeriodEnd).toLocaleDateString()}</dd>
                </div>
              )}
              {status?.cancelAtPeriodEnd && (
                <div>
                  <dt>Cancellation</dt>
                  <dd>Will cancel at the end of the current period.</dd>
                </div>
              )}
            </dl>
            {stripeReady && (
              <button type="button" className="sarnia-btn sarnia-btn--primary" onClick={handlePortal} disabled={redirecting}>
                {redirecting ? 'Opening…' : 'Manage billing'}
              </button>
            )}
          </>
        ) : (
          <p className="bl-status-card__free-note">
            You're on the Free plan. You can build, preview and export sites any time. Choose a paid plan below
            when you'd like Sarnia Digital to get your site live and keep it running.
          </p>
        )}
      </section>

      {requests.length > 0 && (
        <section className="sarnia-card bl-requests">
          <h2>Your plan requests</h2>
          <ul className="bl-requests__list">
            {requests.map((request) => (
              <li key={request.id} className="bl-request">
                <div className="bl-request__main">
                  <strong>{getPlanInfo(request.plan_id)?.name ?? request.plan_id}</strong>
                  <span className={`sarnia-badge ${requestStatusBadgeClass[request.status] ?? 'sarnia-badge--neutral'}`}>
                    {requestStatusLabels[request.status] ?? request.status}
                  </span>
                </div>
                {request.message ? <p className="bl-request__message">{request.message}</p> : null}
                <p className="bl-request__date">
                  Requested {new Date(request.created_at).toLocaleDateString()}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bl-plan-grid" aria-label="Choose a plan">
        <article className="sarnia-card bl-plan-card">
          <div className="bl-plan-card__header">
            <h2>{FREE_PLAN.name}</h2>
            <p className="bl-plan-card__tagline">{FREE_PLAN.tagline}</p>
            <div className="bl-plan-card__price">
              <span>£0</span>
              <span className="bl-plan-card__period">forever</span>
            </div>
          </div>
          <ul className="bl-plan-card__features">
            {FREE_PLAN.features.map((feature) => (
              <li key={feature}>
                <Icon name="check" size={18} className="bl-check" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <div className="bl-plan-card__action">
            <span className="sarnia-btn sarnia-btn--secondary sarnia-btn--lg" aria-disabled="true">
              Your free plan
            </span>
          </div>
        </article>

        {PLANS.map((plan) => (
          <article
            key={plan.id}
            className={`sarnia-card bl-plan-card${plan.highlight ? ' bl-plan-card--highlight' : ''}`}
          >
            {plan.highlight && <span className="bl-plan-card__ribbon">Most popular</span>}
            <div className="bl-plan-card__header">
              <h2>{plan.name}</h2>
              <p className="bl-plan-card__tagline">{plan.tagline}</p>
              <div className="bl-plan-card__price">
                <span>{formatPlanPrice(plan)}</span>
                <span className="bl-plan-card__period">/{plan.interval}</span>
              </div>
            </div>
            <ul className="bl-plan-card__features">
              {plan.features.map((feature) => (
                <li key={feature}>
                  <Icon name="check" size={18} className="bl-check" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <div className="bl-plan-card__action">
              <button
                type="button"
                className={`sarnia-btn sarnia-btn--lg ${
                  plan.highlight ? 'sarnia-btn--primary' : 'sarnia-btn--secondary'
                }`}
                onClick={() => openPanel(plan.id)}
              >
                Choose {plan.name}
              </button>
            </div>
          </article>
        ))}
      </section>

      {panelOpen && selectedPlanId && (
        <section className="sarnia-card bl-panel" aria-labelledby="bl-panel-heading">
          <div className="bl-panel__header">
            <h2 id="bl-panel-heading">
              Choose {getPlanInfo(selectedPlanId)?.name ?? selectedPlanId}
            </h2>
            <button type="button" className="bl-panel__close" onClick={closePanel} aria-label="Close panel">
              <Icon name="x" size={18} />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="bl-form-field">
              <label htmlFor="bl-site">Which site should we get live?</label>
              <select id="bl-site" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">No particular site yet</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="bl-form-field">
              <label htmlFor="bl-message">Tell us what you need (your domain name, deadlines...)</label>
              <textarea
                id="bl-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={1000}
                rows={5}
                placeholder="e.g. I'd like my site live at example.com before the end of the month."
              />
            </div>
            <div className="bl-panel__actions">
              <button type="button" className="sarnia-btn sarnia-btn--secondary" onClick={closePanel}>
                Cancel
              </button>
              <button type="submit" className="sarnia-btn sarnia-btn--primary" disabled={submitting}>
                {submitting ? 'Working…' : stripeReady ? 'Start checkout' : 'Send request'}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
